import {Link} from '@shopify/hydrogen';
import SanityImage from '~/components/media/SanityImage.client';
import {Button} from '~/components/elements';
import clsx from 'clsx';
import {
  FILTER_NAMES,
  SKI_TEST_SORT_CONFIGS,
  filterAbilityLevelValues,
  filterGenderValues,
  filterSkiTypeValues,
} from '~/lib/const';
import {useEffect, useState} from 'react';
import {SkiTestEntity} from '~/types';
import {useRouter} from '@shopify/hydrogen/foundation/Router/BrowserRouter.client';
import {filterBrandValues, filterBrandSnowboardValues} from '~/lib/const';

function FeatureRow({label, value}: {label: string; value: string}) {
  return (
    <div>
      <span>
        <b>{label}:</b> {value}
      </span>
    </div>
  );
}

export function SkiTestItem({ski}: {ski: SkiTestEntity}) {
  const isLink = ski.href != null;
  const WrapperLink = isLink ? Link : 'div';
  const props = isLink ? {to: ski?.href ?? '#'} : {};
  return (
    <div
      className={
        'flex flex-col justify-center items-center font-header border-b-2 border-solid pb-4'
      }
    >
      {ski.title && (
        // @ts-ignore
        <WrapperLink {...props}>
          <h1 className={'font-bold text-lg mb-4'}>
            {ski.title.replace('&#8217;', "'")}
          </h1>
        </WrapperLink>
      )}
      {ski.mainImage && (
        // @ts-ignore
        <WrapperLink {...props}>
          <div className={'w-full'}>
            <SanityImage
              src={ski.mainImage?.asset._ref}
              alt={ski.title ?? 'Ski'}
              layout="responsive"
              objectFit="cover"
              className="rounded-[10px] lg:rounded-none"
            />
          </div>
        </WrapperLink>
      )}
      <div
        className={
          'text-sm flex flex-col justify-center items-center mt-4 mb-2 min-h-[80px]'
        }
      >
        {ski.sidecut && <FeatureRow label="Dimensions" value={ski.sidecut} />}
        {ski.radius && <FeatureRow label="Radius" value={ski.radius} />}
        {ski.lengths && <FeatureRow label="Lengths" value={ski.lengths} />}
        {ski.price && <FeatureRow label="Price" value={`$${ski.price}`} />}
      </div>
      {ski.href && (
        <Button variant={'blue'} size={'sm'} to={ski.href}>
          Read More
        </Button>
      )}
    </div>
  );
}

function applySort(skis: SkiTestEntity[], sort: string | null) {
  const sortFunction = SKI_TEST_SORT_CONFIGS[sort ?? '']?.sort;
  return sortFunction != null ? [...skis].sort(sortFunction) : skis;
}

// The logic is
// OR between in same filter types
// AND between the filter types
function applyFilters(
  skis: SkiTestEntity[],
  categories: string[],
  tags: string[],
) {
  // Group the filters first
  const categoryGrouped = categories.reduce(
    (acc, category) => {
      // Since ski type brands and snowboard brands can't be on the same page,
      // we will use it like this for simplicity for now
      if (
        [...filterBrandValues, ...filterBrandSnowboardValues].includes(category)
      ) {
        acc[FILTER_NAMES.BRANDS].push(category);
      }
      // Add any additional category filters here

      return acc;
    },
    {
      [FILTER_NAMES.BRANDS]: [],
    } as Record<string, string[]>,
  );

  const tagGrouped = tags.reduce(
    (acc, tag) => {
      if (filterAbilityLevelValues.includes(tag)) {
        acc[FILTER_NAMES.ABILITY_LEVEL].push(tag);
      } else if (filterGenderValues.includes(tag)) {
        acc[FILTER_NAMES.GENDER].push(tag);
      } else if (filterSkiTypeValues.includes(tag)) {
        acc[FILTER_NAMES.SKI_TYPE].push(tag);
      }
      // Add any additional tag filters here
      return acc;
    },
    {
      [FILTER_NAMES.ABILITY_LEVEL]: [],
      [FILTER_NAMES.GENDER]: [],
      [FILTER_NAMES.SKI_TYPE]: [],
      // Add default values here
    } as Record<string, string[]>,
  );

  if (categories.length === 0 && tags.length === 0) return skis;

  // OR between in same filter types
  // AND between the filter types
  return skis.filter((ski) => {
    const isCategoryTrue = Object.values(categoryGrouped).every((group) => {
      if (group.length === 0) return true;
      return group.some((i) => ski.categories?.includes(i));
    });

    const isTagFilterTrue = Object.values(tagGrouped).every((group) => {
      if (group.length === 0) return true;
      return group.some((i) => ski.tags?.includes(i));
    });

    return isCategoryTrue && isTagFilterTrue;
  });
}

export default function SkiTestGrid({
  className,
  skis,
}: {
  className?: string;
  skis: SkiTestEntity[];
}) {
  const router = useRouter();
  const [skiState, setSkiState] = useState(skis);

  useEffect(() => {
    const searchParams = new URLSearchParams(router.location.search);

    const sort = searchParams.get('sort');
    const categories = searchParams.get('categories');
    const tags = searchParams.get('tags');
    const categoriesArr = categories?.split(',') ?? [];
    const tagsArr = tags?.split(',') ?? [];

    const filteredSkis = applyFilters(skis, categoriesArr, tagsArr);
    const sortedSkis = applySort(filteredSkis, sort);

    setSkiState(sortedSkis);
  }, [router.location.search, skis]);

  return (
    <>
      {skiState.length === 0 && (
        <h1
          className={'my-10 w-full text-center font-header text-lg font-bold'}
        >
          Sorry, no skis match those selected filters.
        </h1>
      )}
      <div
        className={clsx(
          'grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-8',
          className,
        )}
      >
        {skiState.map((ski) => {
          return <SkiTestItem key={ski.title} ski={ski} />;
        })}
      </div>
    </>
  );
}
